import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Contact, AgentCard } from '@/types/contact.js';

interface ContactStore {
    // State
    contacts: Map<string, Contact>;

    // Actions
    addContact: (agentCard: AgentCard, customData?: Partial<Pick<Contact, 'tags'>>) => string;
    updateContact: (id: string, updates: Partial<Contact>) => void;
    removeContact: (id: string) => void;
    getContact: (id: string) => Contact | undefined;
    getContactByName: (name: string) => Contact | undefined;
    getAllContacts: () => Contact[];
    searchContacts: (query: string) => Contact[];
    toggleFavorite: (id: string) => void;
    updateLastUsed: (id: string) => void;
    clearContacts: () => void;
}

export const useContactStore = create<ContactStore>()(
    persist(
        (set, get) => ({
            // Initial state
            contacts: new Map(),

            // Add a new contact
            addContact: (agentCard, customData) => {
                const id = `contact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const contact: Contact = {
                    id,
                    agentCard,
                    addedAt: Date.now(),
                    isFavorite: false,
                    ...customData,
                };

                set((state) => {
                    const newContacts = new Map(state.contacts);
                    newContacts.set(id, contact);
                    return { contacts: newContacts };
                });

                return id;
            },

            // Update an existing contact
            updateContact: (id, updates) => {
                set((state) => {
                    const contact = state.contacts.get(id);
                    if (!contact) return state;

                    const newContacts = new Map(state.contacts);
                    newContacts.set(id, { ...contact, ...updates });
                    return { contacts: newContacts };
                });
            },

            // Remove a contact
            removeContact: (id) => {
                set((state) => {
                    const newContacts = new Map(state.contacts);
                    newContacts.delete(id);
                    return { contacts: newContacts };
                });
            },

            // Get a single contact by ID
            getContact: (id) => {
                return get().contacts.get(id);
            },

            // Get contact by agent name
            getContactByName: (name) => {
                const contacts = Array.from(get().contacts.values());
                return contacts.find(
                    (c) => c.agentCard.name.toLowerCase() === name.toLowerCase()
                );
            },

            // Get all contacts as array
            getAllContacts: () => {
                return Array.from(get().contacts.values()).sort((a, b) => {
                    // Sort by: favorites first, then by last used, then by added date
                    if (a.isFavorite && !b.isFavorite) return -1;
                    if (!a.isFavorite && b.isFavorite) return 1;
                    if (a.lastUsedAt && b.lastUsedAt) return b.lastUsedAt - a.lastUsedAt;
                    if (a.lastUsedAt) return -1;
                    if (b.lastUsedAt) return 1;
                    return b.addedAt - a.addedAt;
                });
            },

            // Search contacts
            searchContacts: (query) => {
                if (!query) return get().getAllContacts();

                const lowerQuery = query.toLowerCase();
                const contacts = Array.from(get().contacts.values());

                return contacts
                    .filter((contact) => {
                        const nameMatch = contact.agentCard.name.toLowerCase().includes(lowerQuery);
                        const descMatch = contact.agentCard.description
                            .toLowerCase()
                            .includes(lowerQuery);
                        const skillMatch = contact.agentCard.skills.some(
                            (s) =>
                                s.name.toLowerCase().includes(lowerQuery) ||
                                s.description.toLowerCase().includes(lowerQuery) ||
                                s.tags.some((t) => t.toLowerCase().includes(lowerQuery))
                        );
                        const tagMatch = contact.tags?.some((t) =>
                            t.toLowerCase().includes(lowerQuery)
                        );

                        return nameMatch || descMatch || skillMatch || tagMatch;
                    })
                    .sort((a, b) => {
                        // Prioritize name matches
                        const aNameMatch = a.agentCard.name.toLowerCase().startsWith(lowerQuery);
                        const bNameMatch = b.agentCard.name.toLowerCase().startsWith(lowerQuery);

                        if (aNameMatch && !bNameMatch) return -1;
                        if (!aNameMatch && bNameMatch) return 1;

                        // Then by favorites
                        if (a.isFavorite && !b.isFavorite) return -1;
                        if (!a.isFavorite && b.isFavorite) return 1;

                        // Then by last used
                        if (a.lastUsedAt && b.lastUsedAt) return b.lastUsedAt - a.lastUsedAt;
                        return b.addedAt - a.addedAt;
                    });
            },

            // Toggle favorite status
            toggleFavorite: (id) => {
                set((state) => {
                    const contact = state.contacts.get(id);
                    if (!contact) return state;

                    const newContacts = new Map(state.contacts);
                    newContacts.set(id, { ...contact, isFavorite: !contact.isFavorite });
                    return { contacts: newContacts };
                });
            },

            // Update last used timestamp
            updateLastUsed: (id) => {
                set((state) => {
                    const contact = state.contacts.get(id);
                    if (!contact) return state;

                    const newContacts = new Map(state.contacts);
                    newContacts.set(id, { ...contact, lastUsedAt: Date.now() });
                    return { contacts: newContacts };
                });
            },

            // Clear all contacts
            clearContacts: () => {
                set({ contacts: new Map() });
            },
        }),
        {
            name: 'dexto-contacts',
            // Custom serialization for Map
            storage: {
                getItem: (name) => {
                    const str = localStorage.getItem(name);
                    if (!str) return null;
                    const { state } = JSON.parse(str);
                    return {
                        state: {
                            ...state,
                            contacts: new Map(Object.entries(state.contacts || {})),
                        },
                    };
                },
                setItem: (name, value) => {
                    const contactsObj = Object.fromEntries(value.state.contacts);
                    localStorage.setItem(
                        name,
                        JSON.stringify({
                            state: {
                                ...value.state,
                                contacts: contactsObj,
                            },
                        })
                    );
                },
                removeItem: (name) => localStorage.removeItem(name),
            },
        }
    )
);
